import { Alert, AlertIcon, Box, Link } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  type ContractNature,
  type FoaPart,
  ProcedureType,
  type Regulation,
  type ServiceCategory,
} from "../../../../../autogen/bff-api";
import { urls } from "../../../../../urls";
import { isAnnouncedPartI, isPartIHealthSocialEducationService, partRunsInSimplifiedFlow } from "../utils/foaPartDisplay";
import { allowsTenderCompetition, isFoaPartIV } from "../utils/foaPartII";
import { isTwoStageProcedure, runsBelowEeaThreshold } from "../utils/procedure";

interface Props {
  eventId: string;
  procedureType: ProcedureType | null | undefined;
  regulation: Regulation | null | undefined;
  foaPart: FoaPart | null | undefined;
  contractNature: ContractNature | null | undefined;
  serviceCategory: ServiceCategory | null | undefined;
  announcedOnDoffin?: boolean | null;
  onProcedureStep?: boolean;
}

export const ProcedureUnavailableNotice = ({
  eventId,
  procedureType,
  regulation,
  foaPart,
  contractNature,
  serviceCategory,
  announcedOnDoffin,
  onProcedureStep = false,
}: Props) => {
  const { t } = useTranslation();
  if (!procedureType || !foaPart) return null;
  if (!isUnavailableInItsPart({ procedureType, regulation, foaPart, contractNature, serviceCategory, announcedOnDoffin })) {
    return null;
  }

  return (
    <Alert status="warning" mt="3" borderRadius="md" fontSize="sm">
      <AlertIcon />
      <Box flex="1">
        {t("{{procedure}} can't be used in {{part}}. Choose the procedure again.", {
          procedure: t(procedureType),
          part: t(foaPart),
        })}
        {!onProcedureStep && (
          <>
            {" "}
            <Link as={RouterLink} to={urls.app.sourcingEvents.edit.go(eventId).procedure} color="smPrimary">
              {t("Go to Procedure")}
            </Link>
          </>
        )}
      </Box>
    </Alert>
  );
};

export const isUnavailableInItsPart = ({
  procedureType,
  regulation,
  foaPart,
  contractNature,
  serviceCategory,
  announcedOnDoffin,
}: Omit<Props, "eventId" | "onProcedureStep">): boolean => {
  if (!procedureType || !foaPart) return false;
  if (isPartIHealthSocialEducationService(regulation, foaPart, contractNature, serviceCategory)) return false;
  if (isAnnouncedPartI({ regulation, foaPart, announcedOnDoffin })) return false;
  const tenderCompetitionOutsideItsParts =
    PART_II_ONLY_PROCEDURES.includes(procedureType) &&
    !allowsTenderCompetition(regulation, foaPart) &&
    !partRunsInSimplifiedFlow(foaPart);
  const twoStageInPartIV =
    isFoaPartIV(regulation, foaPart) && isTwoStageProcedure(procedureType) && !runsBelowEeaThreshold(procedureType);
  return tenderCompetitionOutsideItsParts || twoStageInPartIV;
};

const PART_II_ONLY_PROCEDURES: readonly ProcedureType[] = [
  ProcedureType.OpenTenderCompetition,
  ProcedureType.RestrictedTenderCompetition,
];
